import React, { useState, useCallback } from 'react';
import MainLayout from '../../components/ui/MainLayout';
import Icon from '../../components/AppIcon';
import { useRBAC } from '../../contexts/RBACContext';

// ─── Default Categories & Cards ──────────────────────────────────────────────
const DEFAULT_CATEGORIES = [
  {
    id: 'cat-1',
    name: 'Campus Navigation',
    icon: 'Map',
    color: '#1A7A4A',
    enabled: true,
    order: 1,
    cards: [
      { id: 'card-1', title: 'Campus Map', description: 'Interactive map of all campus buildings and facilities.', icon: 'Map', path: '/campus-map', enabled: true, order: 1 },
    ],
  },
  {
    id: 'cat-2',
    name: 'Information & Support',
    icon: 'HelpCircle',
    color: '#0E7490',
    enabled: true,
    order: 2,
    cards: [
      { id: 'card-2', title: 'Help Center & FAQ', description: 'Searchable knowledge base and frequently asked questions.', icon: 'HelpCircle', path: '/help-center', enabled: true, order: 1 },
      { id: 'card-3', title: 'Contacts', description: 'Key contacts including administration, security, and support.', icon: 'Phone', path: '/emergency-contacts', enabled: true, order: 2 },
    ],
  },
  {
    id: 'cat-3',
    name: 'Lost & Found',
    icon: 'PackageSearch',
    color: '#7B3FA0',
    enabled: true,
    order: 3,
    cards: [
      { id: 'card-4', title: 'Lost & Found', description: 'Report lost items or claim found items on campus.', icon: 'PackageSearch', path: '/lost-found', enabled: true, order: 1 },
    ],
  },
  {
    id: 'cat-4',
    name: 'My Stay',
    icon: 'History',
    color: '#2B3A8C',
    enabled: true,
    order: 4,
    cards: [
      { id: 'card-5', title: 'Stay History', description: 'View your previous and current stay records.', icon: 'History', path: '/stay-history', enabled: true, order: 1 },
      { id: 'card-6', title: 'Notifications', description: 'In-app notification center for all updates.', icon: 'Bell', path: '/notification-center', enabled: true, order: 2 },
    ],
  },
];

const ICON_OPTIONS = [
  'Map', 'HelpCircle', 'Phone', 'PackageSearch', 'History', 'Bell',
  'Building2', 'ConciergeBell', 'Info', 'Star', 'BookOpen', 'Wrench',
  'UserCheck', 'ArrowLeftRight', 'FileText', 'MessageCircle', 'Shield',
  'Home', 'Settings', 'Calendar', 'Clock', 'Globe', 'Mail',
];

const COLOR_OPTIONS = [
  '#1A7A4A', '#0E7490', '#7B3FA0', '#2B3A8C', '#E05C1A', '#E8A44A',
  '#64748B', '#DC2626', '#0891B2', '#059669', '#D97706', '#7C3AED',
];

// ─── Sub-components ───────────────────────────────────────────────────────────
function CategoryModal({ category, onSave, onClose }) {
  const [name, setName] = useState(category?.name || '');
  const [icon, setIcon] = useState(category?.icon || 'ConciergeBell');
  const [color, setColor] = useState(category?.color || '#0E7490');

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!name?.trim()) return;
    onSave({ name: name?.trim(), icon, color });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-card border border-border rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="font-bold text-foreground text-base">{category ? 'Edit Category' : 'New Category'}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={16} className="text-muted-foreground" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">Category Name *</label>
            <input
              value={name}
              onChange={e => setName(e?.target?.value)}
              placeholder="e.g. Campus Navigation"
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">Icon</label>
            <div className="grid grid-cols-8 gap-1.5">
              {ICON_OPTIONS?.map(ic => (
                <button
                  key={ic} type="button"
                  onClick={() => setIcon(ic)}
                  className={`p-2 rounded-lg border transition-all flex items-center justify-center ${icon === ic ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted'}`}
                  title={ic}
                >
                  <Icon name={ic} size={14} className={icon === ic ? 'text-primary' : 'text-muted-foreground'} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">Color</label>
            <div className="flex flex-wrap gap-2">
              {COLOR_OPTIONS?.map(c => (
                <button
                  key={c} type="button"
                  onClick={() => setColor(c)}
                  className={`w-7 h-7 rounded-full border-2 transition-all ${color === c ? 'border-foreground scale-110' : 'border-transparent'}`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors">Cancel</button>
            <button type="submit" className="flex-1 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CardModal({ card, categoryId, onSave, onClose }) {
  const [title, setTitle] = useState(card?.title || '');
  const [description, setDescription] = useState(card?.description || '');
  const [icon, setIcon] = useState(card?.icon || 'Star');
  const [path, setPath] = useState(card?.path || '');

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!title?.trim()) return;
    onSave({ title: title?.trim(), description: description?.trim(), icon, path: path?.trim() });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-card border border-border rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="font-bold text-foreground text-base">{card ? 'Edit Service Card' : 'New Service Card'}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={16} className="text-muted-foreground" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">Card Title *</label>
            <input
              value={title}
              onChange={e => setTitle(e?.target?.value)}
              placeholder="e.g. Campus Map"
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e?.target?.value)}
              placeholder="Brief description of this service..."
              rows={2}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">Navigation Path</label>
            <input
              value={path}
              onChange={e => setPath(e?.target?.value)}
              placeholder="e.g. /campus-map"
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">Icon</label>
            <div className="grid grid-cols-8 gap-1.5">
              {ICON_OPTIONS?.map(ic => (
                <button
                  key={ic} type="button"
                  onClick={() => setIcon(ic)}
                  className={`p-2 rounded-lg border transition-all flex items-center justify-center ${icon === ic ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted'}`}
                  title={ic}
                >
                  <Icon name={ic} size={14} className={icon === ic ? 'text-primary' : 'text-muted-foreground'} />
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors">Cancel</button>
            <button type="submit" className="flex-1 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ResidentServicesAdmin() {
  const { isAdmin } = useRBAC();
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [expandedCat, setExpandedCat] = useState(null);
  const [catModal, setCatModal] = useState(null); // null | 'new' | category object
  const [cardModal, setCardModal] = useState(null); // null | { categoryId, card? }
  const [deleteConfirm, setDeleteConfirm] = useState(null); // null | { type, catId, cardId? }

  if (!isAdmin) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Icon name="Lock" size={40} className="mx-auto mb-3 text-muted-foreground opacity-50" />
            <p className="font-medium text-foreground">Access Restricted</p>
            <p className="text-sm text-muted-foreground mt-1">This page is for administrators only.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  // ── Category actions ──
  const handleSaveCategory = (data) => {
    if (catModal === 'new') {
      const newCat = {
        id: `cat-${Date.now()}`,
        ...data,
        enabled: true,
        order: categories?.length + 1,
        cards: [],
      };
      setCategories(prev => [...prev, newCat]);
    } else {
      setCategories(prev => prev?.map(c => c?.id === catModal?.id ? { ...c, ...data } : c));
    }
    setCatModal(null);
  };

  const toggleCategory = (catId) => {
    setCategories(prev => prev?.map(c => c?.id === catId ? { ...c, enabled: !c?.enabled } : c));
  };

  const deleteCategory = (catId) => {
    setCategories(prev => prev?.filter(c => c?.id !== catId));
    setDeleteConfirm(null);
  };

  const moveCategoryUp = (catId) => {
    setCategories(prev => {
      const idx = prev?.findIndex(c => c?.id === catId);
      if (idx <= 0) return prev;
      const arr = [...prev];
      [arr[idx - 1], arr[idx]] = [arr?.[idx], arr?.[idx - 1]];
      return arr?.map((c, i) => ({ ...c, order: i + 1 }));
    });
  };

  const moveCategoryDown = (catId) => {
    setCategories(prev => {
      const idx = prev?.findIndex(c => c?.id === catId);
      if (idx >= prev?.length - 1) return prev;
      const arr = [...prev];
      [arr[idx], arr[idx + 1]] = [arr?.[idx + 1], arr?.[idx]];
      return arr?.map((c, i) => ({ ...c, order: i + 1 }));
    });
  };

  // ── Card actions ──
  const handleSaveCard = (catId, data) => {
    const existingCard = cardModal?.card;
    setCategories(prev => prev?.map(c => {
      if (c?.id !== catId) return c;
      if (existingCard) {
        return { ...c, cards: c?.cards?.map(card => card?.id === existingCard?.id ? { ...card, ...data } : card) };
      } else {
        const newCard = {
          id: `card-${Date.now()}`,
          ...data,
          enabled: true,
          order: c?.cards?.length + 1,
        };
        return { ...c, cards: [...c?.cards, newCard] };
      }
    }));
    setCardModal(null);
  };

  const toggleCard = (catId, cardId) => {
    setCategories(prev => prev?.map(c => {
      if (c?.id !== catId) return c;
      return { ...c, cards: c?.cards?.map(card => card?.id === cardId ? { ...card, enabled: !card?.enabled } : card) };
    }));
  };

  const deleteCard = (catId, cardId) => {
    setCategories(prev => prev?.map(c => {
      if (c?.id !== catId) return c;
      return { ...c, cards: c?.cards?.filter(card => card?.id !== cardId)?.map((card, i) => ({ ...card, order: i + 1 })) };
    }));
    setDeleteConfirm(null);
  };

  const moveCardUp = (catId, cardId) => {
    setCategories(prev => prev?.map(c => {
      if (c?.id !== catId) return c;
      const idx = c?.cards?.findIndex(card => card?.id === cardId);
      if (idx <= 0) return c;
      const arr = [...c?.cards];
      [arr[idx - 1], arr[idx]] = [arr?.[idx], arr?.[idx - 1]];
      return { ...c, cards: arr?.map((card, i) => ({ ...card, order: i + 1 })) };
    }));
  };

  const moveCardDown = (catId, cardId) => {
    setCategories(prev => prev?.map(c => {
      if (c?.id !== catId) return c;
      const idx = c?.cards?.findIndex(card => card?.id === cardId);
      if (idx >= c?.cards?.length - 1) return c;
      const arr = [...c?.cards];
      [arr[idx], arr[idx + 1]] = [arr?.[idx + 1], arr?.[idx]];
      return { ...c, cards: arr?.map((card, i) => ({ ...card, order: i + 1 })) };
    }));
  };

  const totalCards = categories?.reduce((sum, c) => sum + c?.cards?.length, 0);
  const enabledCategories = categories?.filter(c => c?.enabled)?.length;
  const enabledCards = categories?.reduce((sum, c) => sum + c?.cards?.filter(card => card?.enabled)?.length, 0);

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Resident Services Management
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage categories and service cards displayed to residents
            </p>
          </div>
          <button
            onClick={() => setCatModal('new')}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors shadow-sm"
          >
            <Icon name="Plus" size={16} />
            New Category
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total Categories', value: categories?.length, icon: 'LayoutGrid', color: 'text-blue-600' },
            { label: 'Active Categories', value: enabledCategories, icon: 'CheckCircle', color: 'text-green-600' },
            { label: 'Total Cards', value: totalCards, icon: 'CreditCard', color: 'text-purple-600' },
            { label: 'Active Cards', value: enabledCards, icon: 'Zap', color: 'text-amber-600' },
          ]?.map(stat => (
            <div key={stat?.label} className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <Icon name={stat?.icon} size={14} className={stat?.color} />
                <span className="text-xs text-muted-foreground">{stat?.label}</span>
              </div>
              <div className="text-2xl font-bold text-foreground">{stat?.value}</div>
            </div>
          ))}
        </div>

        {/* Categories List */}
        <div className="space-y-4">
          {categories?.length === 0 && (
            <div className="bg-card border border-border rounded-2xl py-16 text-center">
              <Icon name="LayoutGrid" size={36} className="mx-auto mb-3 text-muted-foreground opacity-30" />
              <p className="font-medium text-foreground">No categories yet</p>
              <p className="text-sm text-muted-foreground mt-1">Click "New Category" to get started.</p>
            </div>
          )}

          {categories?.map((cat, catIdx) => (
            <div
              key={cat?.id}
              className={`bg-card border rounded-2xl overflow-hidden shadow-sm transition-all ${cat?.enabled ? 'border-border' : 'border-border opacity-60'}`}
            >
              {/* Category Header */}
              <div className="flex items-center gap-3 px-5 py-4">
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: cat?.color }}
                >
                  <Icon name={cat?.icon} size={16} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-foreground text-sm truncate">{cat?.name}</h3>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${cat?.enabled ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-muted text-muted-foreground'}`}>
                      {cat?.enabled ? 'Active' : 'Disabled'}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{cat?.cards?.length} card{cat?.cards?.length !== 1 ? 's' : ''} · Order #{catIdx + 1}</p>
                </div>

                {/* Category Actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => moveCategoryUp(cat?.id)}
                    disabled={catIdx === 0}
                    className="p-1.5 rounded-lg hover:bg-muted transition-colors disabled:opacity-30"
                    title="Move up"
                  >
                    <Icon name="ChevronUp" size={14} className="text-muted-foreground" />
                  </button>
                  <button
                    onClick={() => moveCategoryDown(cat?.id)}
                    disabled={catIdx === categories?.length - 1}
                    className="p-1.5 rounded-lg hover:bg-muted transition-colors disabled:opacity-30"
                    title="Move down"
                  >
                    <Icon name="ChevronDown" size={14} className="text-muted-foreground" />
                  </button>
                  <button
                    onClick={() => toggleCategory(cat?.id)}
                    className={`p-1.5 rounded-lg transition-colors ${cat?.enabled ? 'hover:bg-amber-50 text-amber-600' : 'hover:bg-green-50 text-green-600'}`}
                    title={cat?.enabled ? 'Disable' : 'Enable'}
                  >
                    <Icon name={cat?.enabled ? 'EyeOff' : 'Eye'} size={14} />
                  </button>
                  <button
                    onClick={() => setCatModal(cat)}
                    className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground"
                    title="Edit category"
                  >
                    <Icon name="Pencil" size={14} />
                  </button>
                  <button
                    onClick={() => setDeleteConfirm({ type: 'category', catId: cat?.id })}
                    className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-red-500"
                    title="Delete category"
                  >
                    <Icon name="Trash2" size={14} />
                  </button>
                  <button
                    onClick={() => setExpandedCat(expandedCat === cat?.id ? null : cat?.id)}
                    className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground ml-1"
                    title={expandedCat === cat?.id ? 'Collapse' : 'Expand'}
                  >
                    <Icon name={expandedCat === cat?.id ? 'ChevronUp' : 'ChevronDown'} size={16} />
                  </button>
                </div>
              </div>

              {/* Cards Section */}
              {expandedCat === cat?.id && (
                <div className="border-t border-border">
                  {/* Cards list */}
                  {cat?.cards?.length === 0 ? (
                    <div className="py-8 text-center text-muted-foreground">
                      <Icon name="CreditCard" size={24} className="mx-auto mb-2 opacity-30" />
                      <p className="text-xs">No cards in this category</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-border">
                      {cat?.cards?.map((card, cardIdx) => (
                        <div
                          key={card?.id}
                          className={`flex items-center gap-3 px-5 py-3 ${!card?.enabled ? 'opacity-50' : ''}`}
                        >
                          <div className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                            <Icon name={card?.icon} size={13} className="text-muted-foreground" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-foreground truncate">{card?.title}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${card?.enabled ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-muted text-muted-foreground'}`}>
                                {card?.enabled ? 'Active' : 'Off'}
                              </span>
                            </div>
                            {card?.description && (
                              <p className="text-[10px] text-muted-foreground truncate mt-0.5">{card?.description}</p>
                            )}
                            {card?.path && (
                              <p className="text-[10px] text-primary/70 truncate">{card?.path}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button onClick={() => moveCardUp(cat?.id, card?.id)} disabled={cardIdx === 0} className="p-1 rounded hover:bg-muted transition-colors disabled:opacity-30" title="Move up">
                              <Icon name="ChevronUp" size={12} className="text-muted-foreground" />
                            </button>
                            <button onClick={() => moveCardDown(cat?.id, card?.id)} disabled={cardIdx === cat?.cards?.length - 1} className="p-1 rounded hover:bg-muted transition-colors disabled:opacity-30" title="Move down">
                              <Icon name="ChevronDown" size={12} className="text-muted-foreground" />
                            </button>
                            <button
                              onClick={() => toggleCard(cat?.id, card?.id)}
                              className={`p-1 rounded transition-colors ${card?.enabled ? 'hover:bg-amber-50 text-amber-500' : 'hover:bg-green-50 text-green-500'}`}
                              title={card?.enabled ? 'Disable' : 'Enable'}
                            >
                              <Icon name={card?.enabled ? 'EyeOff' : 'Eye'} size={12} />
                            </button>
                            <button
                              onClick={() => setCardModal({ categoryId: cat?.id, card })}
                              className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground"
                              title="Edit card"
                            >
                              <Icon name="Pencil" size={12} />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm({ type: 'card', catId: cat?.id, cardId: card?.id })}
                              className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-red-400"
                              title="Delete card"
                            >
                              <Icon name="Trash2" size={12} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add card button */}
                  <div className="px-5 py-3 bg-muted/20">
                    <button
                      onClick={() => setCardModal({ categoryId: cat?.id, card: null })}
                      className="flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                    >
                      <Icon name="Plus" size={13} />
                      Add Service Card
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Modals */}
        {catModal !== null && (
          <CategoryModal
            category={catModal === 'new' ? null : catModal}
            onSave={handleSaveCategory}
            onClose={() => setCatModal(null)}
          />
        )}

        {cardModal !== null && (
          <CardModal
            card={cardModal?.card}
            categoryId={cardModal?.categoryId}
            onSave={(data) => handleSaveCard(cardModal?.categoryId, data)}
            onClose={() => setCardModal(null)}
          />
        )}

        {/* Delete Confirm */}
        {deleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-card border border-border rounded-2xl shadow-xl w-full max-w-sm p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
                <Icon name="Trash2" size={20} className="text-red-500" />
              </div>
              <h3 className="font-bold text-foreground text-base mb-1">
                Delete {deleteConfirm?.type === 'category' ? 'Category' : 'Card'}?
              </h3>
              <p className="text-sm text-muted-foreground mb-5">
                {deleteConfirm?.type === 'category' ?'This will permanently delete the category and all its cards.' :'This will permanently delete this service card.'}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 py-2 rounded-xl border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (deleteConfirm?.type === 'category') deleteCategory(deleteConfirm?.catId);
                    else deleteCard(deleteConfirm?.catId, deleteConfirm?.cardId);
                  }}
                  className="flex-1 py-2 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
